if (!customElements.get('awards-carousel')) {
  class AwardsCarousel extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.flkty = null;
      this.slideLabels = new WeakMap();
      this.onResize = () => this.handleResize();
    }

    connectedCallback() {
      this.slider = this.querySelector('.js-awards-flickity');
      if (!this.slider || this.slider.querySelectorAll('[data-slide]').length === 0) return;

      this.cacheSlideLabels();

      if (this.getFlickity()) {
        this.initAccessibility();
        return;
      }

      this.waitForFlickity(() => this.initFlickity());
    }

    disconnectedCallback() {
      document.removeEventListener('theme:resize:width', this.onResize);
      this.destroyFlickity();
    }

    getFlickity() {
      const Flickity = window.theme?.Flickity || window.Flickity;
      if (!Flickity || !this.slider) return null;
      return Flickity.data(this.slider);
    }

    waitForFlickity(callback) {
      if (window.theme?.Flickity || window.Flickity) {
        callback();
        return;
      }

      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        if (window.theme?.Flickity || window.Flickity) {
          clearInterval(timer);
          callback();
        } else if (attempts > 50) {
          clearInterval(timer);
        }
      }, 100);
    }

    getSlideLabel(slide, index, total) {
      const cachedLabel = this.slideLabels.get(slide);
      if (cachedLabel) return cachedLabel;

      const datasetLabel = slide.dataset.slideLabel;
      if (datasetLabel) return datasetLabel;

      const ariaLabel = slide.getAttribute('aria-label');
      if (ariaLabel) return ariaLabel;

      const slideIndex = parseInt(slide.dataset.slideIndex, 10) || index + 1;
      const slidesTotal = parseInt(this.slider?.dataset.slidesTotal, 10) || total;

      return `slide ${slideIndex} of ${slidesTotal}`;
    }

    cacheSlideLabels() {
      if (!this.slider) return;

      const slides = this.slider.querySelectorAll('[data-slide]');
      const total = slides.length;

      slides.forEach((slide, index) => {
        const label = this.getSlideLabel(slide, index, total);
        this.slideLabels.set(slide, label);
        slide.dataset.slideLabel = label;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', label);
      });
    }

    applySlideLabels(cells) {
      cells.forEach((cell, index) => {
        const label = this.getSlideLabel(cell.element, index, cells.length);
        this.slideLabels.set(cell.element, label);
        cell.element.setAttribute('role', 'group');
        cell.element.setAttribute('aria-label', label);
      });
    }

    initFlickity() {
      const Flickity = window.theme?.Flickity || window.Flickity;
      if (!Flickity || !this.slider || this.getFlickity()) return;

      this.cacheSlideLabels();

      const initialIndex = parseInt(this.slider.dataset.initialIndex, 10) || 0;
      const slideCount = this.slider.querySelectorAll('[data-slide]').length;
      const safeInitialIndex = Math.min(Math.max(initialIndex, 0), Math.max(slideCount - 1, 0));

      this.flkty = new Flickity(this.slider, {
        cellSelector: '[data-slide]',
        cellAlign: 'center',
        contain: true,
        wrapAround: false,
        freeScroll: false,
        initialIndex: safeInitialIndex,
        draggable: true,
        pageDots: true,
        prevNextButtons: true,
        accessibility: false,
        on: {
          ready: () => {
            this.localizeButtons();
            this.syncSlidesAccessibility();
          },
          change: () => {
            this.syncSlidesAccessibility();
          },
          settle: () => {
            this.syncSlidesAccessibility();
          },
        },
      });

      document.addEventListener('theme:resize:width', this.onResize);
    }

    handleResize() {
      const flkty = this.getFlickity();
      if (flkty) {
        flkty.resize();
        this.syncSlidesAccessibility();
      }
    }

    localizeButtons() {
      if (!this.slider) return;

      const previousLabel = this.slider.dataset.i18nPrevious;
      const nextLabel = this.slider.dataset.i18nNext;
      const previousButton = this.slider.querySelector('.flickity-prev-next-button.previous');
      const nextButton = this.slider.querySelector('.flickity-prev-next-button.next');

      if (previousButton && previousLabel) {
        previousButton.setAttribute('aria-label', previousLabel);
      }

      if (nextButton && nextLabel) {
        nextButton.setAttribute('aria-label', nextLabel);
      }
    }

    syncSlidesAccessibility() {
      const flkty = this.getFlickity();
      if (!flkty) return;

      this.applySlideLabels(flkty.cells);

      flkty.cells.forEach((cell) => {
        const isVisible = cell.element.classList.contains('is-selected');

        cell.element.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

        if (window.theme?.focusable) {
          cell.element.querySelectorAll(window.theme.focusable).forEach((el) => {
            el.setAttribute('tabindex', isVisible ? '0' : '-1');
          });
        }
      });
    }

    resetSlidesAccessibility() {
      if (!this.slider) return;

      this.slider.querySelectorAll('[data-slide]').forEach((slide) => {
        slide.removeAttribute('aria-hidden');

        if (window.theme?.focusable) {
          slide.querySelectorAll(window.theme.focusable).forEach((el) => {
            el.removeAttribute('tabindex');
          });
        }
      });
    }

    initAccessibility() {
      this.cacheSlideLabels();
      this.localizeButtons();
      this.syncSlidesAccessibility();
    }

    destroyFlickity() {
      this.resetSlidesAccessibility();

      const flkty = this.getFlickity();
      if (flkty) {
        flkty.destroy();
      }

      this.flkty = null;
    }
  }

  customElements.define('awards-carousel', AwardsCarousel);
}
